import {
  useEffect,
  useId,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Clock, Search, Tag, Utensils, X as XIcon } from 'lucide-react';
import {
  CATALOG_MEAL_TYPES,
  CATALOG_TAGS,
  catalogMealTypeLabel,
  catalogTagLabel,
  pilaBadgeClass,
  pilaLabel,
  useRecipeCatalog,
  type CatalogMealType,
  type PilaLevel,
  type RecipeCatalogItem,
  type RecipeCatalogIngredient,
} from '@/lib/nutrition';

/**
 * Vista "Catálogo" del módulo Nutrición (M2.36).
 *
 * Decisiones:
 *  - Filtros server-side (la queryKey de `useRecipeCatalog` cambia y
 *    re-fetchea cada combinación). La búsqueda por título también va al
 *    backend para que el seed pueda devolver una sublista pequeña sin
 *    cargar las 40+ recetas al cliente.
 *  - Sin paginación en M2.36: el catálogo seedado tiene ~40 recetas. Si
 *    crece (>100), añadir `limit/offset` al endpoint y un botón "Cargar
 *    más" al final de la grid; el cambio es transparente para los demás
 *    componentes porque seguimos devolviendo un array.
 *  - Click en card abre modal con detalle (ingredientes + pasos). Reusamos
 *    el patrón `Modal` interno (mismo que `RecipesView`) — no extraemos a
 *    un componente compartido en este pase para evitar riesgo de tocar
 *    otros tabs.
 *  - Empty state distinto si el catálogo está vacío en el backend (seed no
 *    cargado) vs. filtros sin resultados.
 */
export function RecipeCatalogView(): ReactElement {
  const [pila, setPila] = useState<PilaLevel | 'all'>('all');
  const [mealTypes, setMealTypes] = useState<CatalogMealType[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<RecipeCatalogItem | null>(null);

  const query = useRecipeCatalog({
    pila,
    mealType: mealTypes,
    tags,
    q,
  });

  const recipes = query.data ?? [];

  // Heurística para distinguir "catálogo vacío" vs. "filtros sin resultados":
  // si NO hay filtros activos y la respuesta es vacía → el seed no se cargó.
  const hasActiveFilters =
    pila !== 'all' ||
    mealTypes.length > 0 ||
    tags.length > 0 ||
    q.trim().length > 0;

  function toggleMealType(t: CatalogMealType) {
    setMealTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  }

  function toggleTag(t: string) {
    setTags((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  }

  function clearAll() {
    setPila('all');
    setMealTypes([]);
    setTags([]);
    setQ('');
  }

  return (
    <section aria-label="Catálogo de recetas" className="space-y-4">
      <CatalogFilters
        pila={pila}
        onPilaChange={setPila}
        mealTypes={mealTypes}
        onToggleMealType={toggleMealType}
        tags={tags}
        onToggleTag={toggleTag}
        q={q}
        onQChange={setQ}
        onClearAll={clearAll}
      />

      {query.isLoading && (
        <p role="status" aria-live="polite" className="text-slate-700">
          Cargando catálogo…
        </p>
      )}

      {query.isError && (
        <p
          role="alert"
          className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
        >
          No se pudo cargar el catálogo. {query.error?.message ?? ''}
        </p>
      )}

      {query.data && recipes.length === 0 && !hasActiveFilters && (
        <p
          data-testid="catalog-empty-seed"
          className="rounded-md border border-dashed border-slate-300 bg-white px-6 py-10 text-center text-sm text-slate-600"
        >
          El catálogo se cargará al primer arranque. Reinicia el servicio.
        </p>
      )}

      {query.data && recipes.length === 0 && hasActiveFilters && (
        <p
          data-testid="catalog-empty-filtered"
          className="rounded-md border border-dashed border-slate-300 bg-white px-6 py-10 text-center text-sm text-slate-600"
        >
          No hay recetas que coincidan con tus filtros.
        </p>
      )}

      {recipes.length > 0 && (
        <ul
          role="list"
          aria-label="Recetas del catálogo"
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
        >
          {recipes.map((r) => (
            <CatalogCard
              key={String(r.id)}
              recipe={r}
              onOpen={() => setSelected(r)}
            />
          ))}
        </ul>
      )}

      {selected && (
        <CatalogDetailModal
          recipe={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Filtros
// ---------------------------------------------------------------------------

function CatalogFilters({
  pila,
  onPilaChange,
  mealTypes,
  onToggleMealType,
  tags,
  onToggleTag,
  q,
  onQChange,
  onClearAll,
}: {
  pila: PilaLevel | 'all';
  onPilaChange: (p: PilaLevel | 'all') => void;
  mealTypes: CatalogMealType[];
  onToggleMealType: (t: CatalogMealType) => void;
  tags: string[];
  onToggleTag: (t: string) => void;
  q: string;
  onQChange: (v: string) => void;
  onClearAll: () => void;
}): ReactElement {
  return (
    <section
      aria-label="Filtros del catálogo"
      className="space-y-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
    >
      {/* Pila */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
          PILA
        </p>
        <div className="mt-1 flex flex-wrap gap-1.5" role="group" aria-label="Filtrar por pila">
          {(['all', 1, 2, 3] as const).map((opt) => {
            const active = pila === opt;
            const label =
              opt === 'all' ? 'Todas' : pilaLabel(opt as PilaLevel);
            return (
              <button
                key={String(opt)}
                type="button"
                onClick={() => onPilaChange(opt)}
                aria-pressed={active}
                className={`rounded-full px-3 py-1 text-xs font-medium ring-1 transition focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 ${
                  active
                    ? opt === 'all'
                      ? 'bg-teal-600 text-white ring-teal-700'
                      : pilaBadgeClass(opt as PilaLevel) + ' ring-offset-1'
                    : 'bg-white text-slate-700 ring-slate-300 hover:bg-slate-50'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Meal types */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
          Tipo de comida
        </p>
        <div
          className="mt-1 flex flex-wrap gap-1.5"
          role="group"
          aria-label="Filtrar por tipo de comida"
        >
          {CATALOG_MEAL_TYPES.map((t) => {
            const active = mealTypes.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => onToggleMealType(t)}
                aria-pressed={active}
                className={`rounded-full px-3 py-1 text-xs font-medium ring-1 transition focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 ${
                  active
                    ? 'bg-teal-600 text-white ring-teal-700'
                    : 'bg-white text-slate-700 ring-slate-300 hover:bg-slate-50'
                }`}
              >
                {catalogMealTypeLabel(t)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tags */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
          Etiquetas
        </p>
        <div
          className="mt-1 flex flex-wrap gap-1.5"
          role="group"
          aria-label="Filtrar por etiqueta"
        >
          {CATALOG_TAGS.map((t) => {
            const active = tags.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => onToggleTag(t)}
                aria-pressed={active}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ring-1 transition focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 ${
                  active
                    ? 'bg-teal-600 text-white ring-teal-700'
                    : 'bg-white text-slate-700 ring-slate-300 hover:bg-slate-50'
                }`}
              >
                <Tag aria-hidden="true" className="h-3 w-3" />
                {catalogTagLabel(t)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Buscar + limpiar */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label
            htmlFor="catalog-search"
            className="block text-xs font-semibold uppercase tracking-wide text-slate-600"
          >
            Buscar por título
          </label>
          <div className="relative mt-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <input
              id="catalog-search"
              type="search"
              value={q}
              onChange={(e) => onQChange(e.target.value)}
              placeholder="Ej: avena, lentejas, salmón…"
              className="block w-full rounded-md border border-slate-300 bg-white pl-8 pr-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={onClearAll}
          className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        >
          Limpiar filtros
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Card del catálogo
// ---------------------------------------------------------------------------

function CatalogCard({
  recipe,
  onOpen,
}: {
  recipe: RecipeCatalogItem;
  onOpen: () => void;
}): ReactElement {
  const total = recipe.prepMin + recipe.cookMin;
  // Mostramos hasta 2 tags principales en la card; el resto se ve en el modal.
  const visibleTags = recipe.tags.slice(0, 2);
  return (
    <li className="flex flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <button
        type="button"
        onClick={onOpen}
        className="text-left focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        aria-label={`Ver detalle de ${recipe.title}`}
      >
        <h3 className="text-base font-semibold text-slate-900 hover:text-teal-700">
          {recipe.title}
        </h3>
      </button>

      <ul className="mt-2 flex flex-wrap gap-1">
        <li
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${pilaBadgeClass(recipe.pilaLevel)}`}
        >
          {pilaLabel(recipe.pilaLevel)}
        </li>
        {recipe.mealTypes.slice(0, 2).map((mt) => (
          <li
            key={`mt-${mt}`}
            className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 text-xs text-teal-800 ring-1 ring-teal-200"
          >
            <Utensils aria-hidden="true" className="h-3 w-3" />
            {catalogMealTypeLabel(mt as CatalogMealType)}
          </li>
        ))}
        {visibleTags.map((t) => (
          <li
            key={`tag-${t}`}
            className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 ring-1 ring-slate-200"
          >
            {catalogTagLabel(t)}
          </li>
        ))}
      </ul>

      <p className="mt-2 text-sm font-medium text-slate-900">
        {recipe.macros.kcal} kcal
        <span className="ml-2 text-xs font-normal text-slate-600">
          · P {recipe.macros.protG}g · C {recipe.macros.choG}g · G{' '}
          {recipe.macros.fatG}g
        </span>
      </p>

      {total > 0 && (
        <p className="mt-1 inline-flex items-center gap-1 text-xs text-slate-600">
          <Clock aria-hidden="true" className="h-3 w-3" /> {total} min total
          <span className="text-slate-400">
            (prep {recipe.prepMin} · cocción {recipe.cookMin})
          </span>
        </p>
      )}

      <div className="mt-3 flex items-center justify-end gap-2 border-t border-slate-100 pt-2">
        <button
          type="button"
          onClick={onOpen}
          className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        >
          Ver receta
        </button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Modal con detalle
// ---------------------------------------------------------------------------

function CatalogDetailModal({
  recipe,
  onClose,
}: {
  recipe: RecipeCatalogItem;
  onClose: () => void;
}): ReactElement {
  return (
    <Modal title={recipe.title} onClose={onClose}>
      <div className="space-y-4 text-sm text-slate-800">
        <p className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${pilaBadgeClass(recipe.pilaLevel)}`}
          >
            {pilaLabel(recipe.pilaLevel)}
          </span>
          {recipe.mealTypes.map((mt) => (
            <span
              key={`mod-mt-${mt}`}
              className="rounded-full bg-teal-50 px-2 py-0.5 text-xs text-teal-800 ring-1 ring-teal-200"
            >
              {catalogMealTypeLabel(mt as CatalogMealType)}
            </span>
          ))}
          <span className="ml-1">
            {recipe.servingsDefault}{' '}
            {recipe.servingsDefault === 1 ? 'ración' : 'raciones'}
          </span>
          {recipe.prepMin + recipe.cookMin > 0 && (
            <span>
              · <Clock aria-hidden="true" className="inline h-3 w-3 align-text-bottom" />{' '}
              {recipe.prepMin + recipe.cookMin} min
            </span>
          )}
        </p>

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Macros por ración
          </h4>
          <div className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            <MacroBox label="Kcal" value={recipe.macros.kcal} />
            <MacroBox label="Proteína" value={recipe.macros.protG} suffix="g" />
            <MacroBox label="Carbohidratos" value={recipe.macros.choG} suffix="g" />
            <MacroBox label="Grasas" value={recipe.macros.fatG} suffix="g" />
          </div>
        </section>

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Ingredientes
          </h4>
          {recipe.ingredients.length === 0 ? (
            <p className="mt-2 text-sm text-slate-600">Sin ingredientes.</p>
          ) : (
            <ul className="mt-2 space-y-1 text-sm">
              {recipe.ingredients.map((ing, idx) => (
                <IngredientLineCatalog key={idx} ing={ing} />
              ))}
            </ul>
          )}
        </section>

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Pasos
          </h4>
          {recipe.steps.length === 0 ? (
            <p className="mt-2 text-sm text-slate-600">Sin pasos definidos.</p>
          ) : (
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-800">
              {recipe.steps.map((s, idx) => (
                <li key={idx}>{s}</li>
              ))}
            </ol>
          )}
        </section>

        {recipe.tags.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Etiquetas
            </h4>
            <ul className="mt-2 flex flex-wrap gap-1">
              {recipe.tags.map((t) => (
                <li
                  key={`mod-tag-${t}`}
                  className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 ring-1 ring-slate-200"
                >
                  {catalogTagLabel(t)}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <footer className="mt-6 flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
        >
          Cerrar
        </button>
      </footer>
    </Modal>
  );
}

function MacroBox({
  label,
  value,
  suffix,
}: {
  label: string;
  value: number;
  suffix?: string;
}): ReactElement {
  return (
    <div className="rounded-md bg-slate-50 p-2">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-slate-900">
        {value}
        {suffix ? ` ${suffix}` : ''}
      </p>
    </div>
  );
}

function IngredientLineCatalog({
  ing,
}: {
  ing: RecipeCatalogIngredient;
}): ReactElement {
  return (
    <li className="flex flex-wrap items-baseline gap-2">
      <span className="font-medium">{ing.name}</span>
      <span className="text-slate-600">— {ing.quantityG} g</span>
      <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-600 ring-1 ring-slate-200">
        {ing.category}
      </span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Modal base (mismo patrón que RecipesView para no acoplar)
// ---------------------------------------------------------------------------

export function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}): ReactElement {
  const titleId = useId();

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-6"
      onKeyDown={handleKeyDown}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
      >
        <header className="mb-4 flex items-start justify-between gap-4 border-b border-slate-200 pb-3">
          <h2 id={titleId} className="text-lg font-semibold text-slate-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-600"
          >
            <XIcon aria-hidden="true" className="h-4 w-4" />
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

